/* ==========================================================================
   Premium Homepage layer
   Added: 2026-08-28. Loaded ONLY on template == 'index' via
   snippets/premium-homepage.liquid. Depends on GSAP + ScrollTrigger + Lenis
   being loaded first (see that snippet).

   REVERT: see the note at the top of premium-homepage.css.
   ========================================================================== */

(function () {
	"use strict";

	document.documentElement.classList.add("premium-home");

	var prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	var isMobile = window.matchMedia("(max-width: 767px)").matches;
	var hasGsap = typeof window.gsap !== "undefined" && typeof window.ScrollTrigger !== "undefined";

	if (hasGsap) {
		gsap.registerPlugin(ScrollTrigger);
	}

	/* ------------------------------ Smooth scroll (Lenis) ------------------------------ */
	var lenis = null;

	function initLenis() {
		if (prefersReducedMotion || typeof window.Lenis === "undefined") return;

		lenis = new window.Lenis({
			duration: 1.1,
			smoothWheel: true,
			smoothTouch: false
		});

		lenis.on("scroll", function () {
			if (hasGsap) ScrollTrigger.update();
		});

		/* fix (2026-08-28 round 7): Lenis was being driven by TWO separate
		   rAF loops at once (this function's own requestAnimationFrame loop,
		   AND gsap.ticker) -- updating it twice per frame with two different,
		   slightly-out-of-sync timestamps. That's exactly what produced the
		   stutter/"stuck" feeling, most noticeable during the Ritual
		   Spotlight pin since pins are the most sensitive to scroll-sync
		   jitter. Only one driver should ever call lenis.raf() per frame. */
		if (hasGsap) {
			gsap.ticker.add(function (time) {
				lenis.raf(time * 1000);
			});
			gsap.ticker.lagSmoothing(0);
		} else {
			(function raf(time) {
				lenis.raf(time);
				requestAnimationFrame(raf);
			})();
		}
	}

	/* ------------------------------ Scroll reveal ------------------------------ */
	function initReveal() {
		var candidates = document.querySelectorAll(
			".collection-list__grid-item, .our-story__frame, " +
			".social-gallery__image-col, .featured-collection__collection, " +
			".featured-collection__products, .stores-locator__container > *"
		);

		if (!candidates.length) return;

		candidates.forEach(function (el, index) {
			el.classList.add("phx-reveal");
			el.style.setProperty("--phx-delay", (index % 6) * 0.08 + "s");
		});

		var observer = new IntersectionObserver(
			function (entries, obs) {
				entries.forEach(function (entry) {
					if (entry.isIntersecting) {
						entry.target.classList.add("is-visible");
						obs.unobserve(entry.target);
					}
				});
			},
			{ rootMargin: "-10% 0px -10% 0px", threshold: 0.05 }
		);

		candidates.forEach(function (el) {
			observer.observe(el);
		});
	}

	/* ------------------------------ Hero exit zoom ------------------------------ */
	function initHero() {
		if (!hasGsap) return;

		var heroWrapper = document.querySelector(".slideshow-wrapper");
		var heroInner = heroWrapper && heroWrapper.querySelector(".slideshow__slider");
		if (!heroWrapper || !heroInner) return;

		gsap.fromTo(
			heroInner,
			{ scale: 1 },
			{
				scale: 1.12,
				ease: "none",
				scrollTrigger: {
					trigger: heroWrapper,
					start: "top top",
					end: "bottom top",
					scrub: true
				}
			}
		);
	}

	/* ------------------------------ Banner parallax + magnetic CTA ------------------------------ */
	function initBanners() {
		if (!hasGsap) return;

		document.querySelectorAll(".banner__media-wrapper").forEach(function (wrapper) {
			var media = wrapper.querySelector(".banner__media");
			if (!media) return;

			gsap.fromTo(
				media,
				{ yPercent: -8 },
				{
					yPercent: 8,
					ease: "none",
					scrollTrigger: {
						trigger: wrapper,
						start: "top bottom",
						end: "bottom top",
						scrub: true
					}
				}
			);
		});

		document.querySelectorAll(".banner__content").forEach(function (content) {
			gsap.fromTo(
				content,
				{ y: 60 },
				{
					y: -30,
					ease: "none",
					scrollTrigger: {
						trigger: content.closest(".banner"),
						start: "top bottom",
						end: "bottom top",
						scrub: true
					}
				}
			);
		});

		if (isMobile) return;

		document.querySelectorAll(".banner__button, .ritual-spotlight a.button").forEach(function (btn) {
			btn.classList.add("phx-magnetic");

			btn.addEventListener("mousemove", function (event) {
				var rect = btn.getBoundingClientRect();
				var x = event.clientX - rect.left - rect.width / 2;
				var y = event.clientY - rect.top - rect.height / 2;
				gsap.to(btn, { x: x * 0.35, y: y * 0.35, duration: 0.3, ease: "power2.out" });
			});

			btn.addEventListener("mouseleave", function () {
				gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: "elastic.out(1, 0.4)" });
			});
		});
	}

	/* ------------------------------ Featured products: image-only drift ------------------------------
	   Fix (2026-08-28): this used to drift the whole card, which pushed the
	   price/title out of the swiper's clipped viewport on some cards. Now it
	   only drifts the image *inside* its own already-cropped image wrapper,
	   so text never moves and never gets cut off. */
	function initFeaturedProducts() {
		if (!hasGsap || isMobile) return;

		var slides = document.querySelectorAll(".featured-products .js-featured-products-slide");
		if (!slides.length) return;

		var section = document.querySelector(".featured-products");

		slides.forEach(function (slide, index) {
			var image = slide.querySelector(".product-card__image-wrapper img, .product-card__image");
			if (!image) return;

			var direction = index % 2 === 0 ? 1 : -1;

			gsap.fromTo(
				image,
				{ yPercent: -6 * direction },
				{
					yPercent: 6 * direction,
					ease: "none",
					scrollTrigger: {
						trigger: section,
						start: "top bottom",
						end: "bottom top",
						scrub: true
					}
				}
			);
		});
	}

	/* ------------------------------ Coca-Cola-style product "settle" ------------------------------
	   As the Featured Products section approaches, its first product card
	   scales up slightly and rides ahead of the scroll, then eases down to
	   its normal size and the effect ends -- scroll-scrubbed, not pinned.

	   Fix (2026-08-28): this used to also flip the swiper to overflow:visible
	   so the scale-up wasn't clipped, but that let the enlarged card bleed
	   upward over the section heading. Now it stays modest enough to remain
	   fully inside the swiper's own (always-on) clipping box -- clipped at
	   the slider's edge like everything else in the carousel, never reaching
	   the heading above it. */
	function initProductSettle() {
		if (!hasGsap || isMobile || prefersReducedMotion) return;

		var section = document.querySelector(".featured-products");
		var firstSlide = section && section.querySelector(".js-featured-products-slide");
		if (!section || !firstSlide) return;

		gsap.set(firstSlide, { transformOrigin: "50% 50%" });

		gsap.fromTo(
			firstSlide,
			{ scale: 1.08 },
			{
				scale: 1,
				ease: "none",
				scrollTrigger: {
					trigger: section,
					start: "top 80%",
					end: "top 40%",
					scrub: 0.6
				}
			}
		);
	}

	/* ------------------------------ Ticker speed tied to scroll ------------------------------ */
	function initTicker() {
		var track = document.querySelector(".js-ticker-container");
		if (!track || prefersReducedMotion) return;

		var computed = getComputedStyle(track);
		var baseDesktop = parseFloat(computed.getPropertyValue("--desktop-movement-speed")) || 20;
		var baseMobile = parseFloat(computed.getPropertyValue("--mobile-movement-speed")) || 20;
		var lastY = window.scrollY;
		var resetTimer = null;

		window.addEventListener(
			"scroll",
			function () {
				var currentY = window.scrollY;
				var velocity = Math.abs(currentY - lastY);
				lastY = currentY;

				var speedFactor = Math.max(0.35, 1 - Math.min(velocity, 60) / 90);
				track.style.setProperty("--desktop-movement-speed", (baseDesktop * speedFactor) + "s");
				track.style.setProperty("--mobile-movement-speed", (baseMobile * speedFactor) + "s");

				clearTimeout(resetTimer);
				resetTimer = setTimeout(function () {
					track.style.setProperty("--desktop-movement-speed", baseDesktop + "s");
					track.style.setProperty("--mobile-movement-speed", baseMobile + "s");
				}, 500);
			},
			{ passive: true }
		);
	}

	/* ------------------------------ Featured collection tilt ------------------------------ */
	function initTilt() {
		if (isMobile) return;

		document.querySelectorAll(".featured-collection__collection").forEach(function (card) {
			var inner = card.firstElementChild;
			if (!inner) return;

			card.addEventListener("mousemove", function (event) {
				var rect = card.getBoundingClientRect();
				var px = (event.clientX - rect.left) / rect.width - 0.5;
				var py = (event.clientY - rect.top) / rect.height - 0.5;
				inner.style.transform = "rotateY(" + px * 8 + "deg) rotateX(" + py * -8 + "deg)";
			});

			card.addEventListener("mouseleave", function () {
				inner.style.transform = "rotateY(0deg) rotateX(0deg)";
			});
		});
	}

	/* ------------------------------ Image with text: content slides in as you scroll ------------------------------
	   The image panel itself pins via CSS (see .image-with-text__image-container
	   in the stylesheet); here the text column eases in from the side as the
	   section scrolls into view -- a real scroll-driven movement, not just a
	   one-shot fade. */
	function initImageWithText() {
		if (!hasGsap || isMobile) return;

		document.querySelectorAll(".image-with-text").forEach(function (section) {
			var content = section.querySelector(".image-with-text__container > *:not(.image-with-text__image-container)");
			if (!content) return;

			/* fix (2026-08-28): start/end used to sit right at the edge of the
			   viewport, so the slide-in was mostly finished before the section
			   was actually visible. Delayed + slowed (start later, longer
			   scrub lag, bigger travel distance) so it plays out on-screen. */
			gsap.fromTo(
				content,
				{ x: 110, opacity: 0.25 },
				{
					x: 0,
					opacity: 1,
					ease: "none",
					scrollTrigger: {
						trigger: section,
						start: "top 55%",
						end: "top 5%",
						scrub: 1.4
					}
				}
			);
		});
	}

	/* ------------------------------ Closing banner emphasis ------------------------------ */
	function initClosingBanner() {
		if (!hasGsap) return;

		var banners = document.querySelectorAll(".banner__content");
		var last = banners[banners.length - 1];
		if (!last) return;

		gsap.fromTo(
			last,
			{ scale: 0.92, opacity: 0.6 },
			{
				scale: 1,
				opacity: 1,
				ease: "none",
				scrollTrigger: {
					trigger: last.closest(".banner"),
					start: "top 90%",
					end: "top 45%",
					scrub: 0.6
				}
			}
		);
	}

	/* ------------------------------ Our story ------------------------------
	   fix (2026-08-28 round 14): removed entirely. This used to pin the
	   section and GSAP-scrub a horizontal image strip -- after several
	   rounds of bugs (residual motion after scrolling stopped, the pin
	   overlapping the next section, mobile misalignment), the section itself
	   was rewritten (sections/our-story.liquid) to use plain CSS
	   `position: sticky` for the text and a normal in-flow image gallery
	   instead. That has zero JS and zero animation lag by construction, so
	   there's no longer a function here to own. */

	/* ------------------------------ Product banner is left to its own pinning; float handled in CSS ------------------------------ */

	/* ------------------------------ Social gallery differential column drift ------------------------------ */
	function initSocialGallery() {
		if (!hasGsap || isMobile) return;

		var cols = document.querySelectorAll(".social-gallery__image-col");
		if (!cols.length) return;

		cols.forEach(function (col, index) {
			var direction = index % 2 === 0 ? 1 : -1;

			gsap.fromTo(
				col,
				{ y: 40 * direction },
				{
					y: -40 * direction,
					ease: "none",
					scrollTrigger: {
						trigger: col.closest(".social-gallery"),
						start: "top bottom",
						end: "bottom top",
						scrub: true
					}
				}
			);
		});
	}

	/* ------------------------------ Ritual spotlight (new section) pin + card swap ------------------------------ */
	function initRitualSpotlight() {
		var section = document.querySelector(".js-ritual-spotlight");
		if (!section) return;

		var cards = section.querySelectorAll(".ritual-spotlight__card");
		var dots = section.querySelectorAll(".ritual-spotlight__dot");
		var stage = section.querySelector(".ritual-spotlight__stage");
		if (!cards.length) return;

		var currentIndex = 0;

		function activate(index) {
			currentIndex = index;
			cards.forEach(function (card, i) {
				card.classList.toggle("is-active", i === index);
			});
			dots.forEach(function (dot, i) {
				dot.classList.toggle("is-active", i === index);
			});
		}

		activate(0);

		dots.forEach(function (dot, i) {
			dot.addEventListener("click", function () {
				activate(i);
			});
		});

		/* Swipe / drag left-right to step through cards -- works on both
		   mobile (primary interaction, no scroll-pin there) and desktop
		   (a manual override alongside the scroll-driven pin below). */
		if (stage) {
			var dragStartX = 0;
			var dragStartY = 0;
			var dragging = false;
			var dragLocked = null; /* true = horizontal swipe, false = vertical scroll -- decided once per gesture */

			var onDragStart = function (x, y) {
				dragStartX = x;
				dragStartY = y;
				dragging = true;
				dragLocked = null;
			};

			var onDragMove = function (x, y, event) {
				if (!dragging) return;
				var dx = x - dragStartX;
				var dy = y - dragStartY;

				if (dragLocked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
					dragLocked = Math.abs(dx) > Math.abs(dy);
				}

				if (dragLocked && event && event.cancelable) {
					event.preventDefault();
				}
			};

			var onDragEnd = function (x) {
				if (!dragging) return;
				dragging = false;

				if (dragLocked) {
					var dx = x - dragStartX;
					var threshold = 40;

					if (dx <= -threshold && currentIndex < cards.length - 1) {
						activate(currentIndex + 1);
					} else if (dx >= threshold && currentIndex > 0) {
						activate(currentIndex - 1);
					}
				}

				dragLocked = null;
			};

			stage.addEventListener(
				"touchstart",
				function (event) {
					var touch = event.touches[0];
					onDragStart(touch.clientX, touch.clientY);
				},
				{ passive: true }
			);

			stage.addEventListener(
				"touchmove",
				function (event) {
					var touch = event.touches[0];
					onDragMove(touch.clientX, touch.clientY, event);
				},
				{ passive: false }
			);

			stage.addEventListener(
				"touchend",
				function (event) {
					var touch = event.changedTouches[0];
					onDragEnd(touch.clientX);
				},
				{ passive: true }
			);

			stage.addEventListener("mousedown", function (event) {
				onDragStart(event.clientX, event.clientY);
				stage.classList.add("is-dragging");
			});

			window.addEventListener("mousemove", function (event) {
				onDragMove(event.clientX, event.clientY, event);
			});

			window.addEventListener("mouseup", function (event) {
				if (!dragging) return;
				stage.classList.remove("is-dragging");
				onDragEnd(event.clientX);
			});
		}

		if (!hasGsap || isMobile || prefersReducedMotion) {
			return;
		}

		var pin = section.querySelector(".ritual-spotlight__pin");

		ScrollTrigger.create({
			trigger: section,
			start: "top top",
			end: "+=" + cards.length * 400,
			scrub: 0.4,
			pin: pin,
			anticipatePin: 1,
			onUpdate: function (self) {
				var index = Math.min(cards.length - 1, Math.floor(self.progress * cards.length));
				activate(index);
			}
		});
	}

	/* ------------------------------ Floating ambient orbs ------------------------------
	   Soft blurred colour orbs planted in a few sections. Each drifts slowly
	   as you scroll past its section (GSAP scrub) AND nudges gently toward
	   the cursor (independent rAF loop on an inner element) -- two separate
	   transformed layers so the two motions never fight each other. */
	function initFloatingOrbs() {
		if (isMobile || prefersReducedMotion) return;

		var bannerContents = document.querySelectorAll(".banner__content");
		var lastBannerContent = bannerContents[bannerContents.length - 1];

		var hosts = [
			{ el: document.querySelector(".slideshow-wrapper .slideshow"), tone: "sage", size: 320, top: "10%", left: "8%" },
			{ el: document.querySelector(".our-story"), tone: "gold", size: 260, top: "15%", left: "82%" },
			{ el: document.querySelector(".js-ritual-spotlight"), tone: "terracotta", size: 300, top: "60%", left: "6%" },
			{ el: lastBannerContent, tone: "sage", size: 280, top: "-10%", left: "78%" }
		];

		var orbs = [];

		hosts.forEach(function (host, index) {
			var container = host.el;
			if (!container) return;

			var computedPosition = getComputedStyle(container).position;
			if (computedPosition === "static") {
				container.style.position = "relative";
			}

			var outer = document.createElement("div");
			outer.className = "phx-orb-outer";
			outer.setAttribute("aria-hidden", "true");

			var inner = document.createElement("div");
			inner.className = "phx-orb phx-orb--" + host.tone;
			inner.style.width = host.size + "px";
			inner.style.height = host.size + "px";
			inner.style.top = host.top;
			inner.style.left = host.left;

			outer.appendChild(inner);
			container.appendChild(outer);
			orbs.push({ el: inner, depth: 0.6 + (index % 3) * 0.3 });

			if (hasGsap) {
				gsap.fromTo(
					outer,
					{ y: -50 },
					{
						y: 50,
						ease: "none",
						scrollTrigger: {
							trigger: container,
							start: "top bottom",
							end: "bottom top",
							scrub: true
						}
					}
				);
			}
		});

		if (!orbs.length) return;

		var mouseX = window.innerWidth / 2;
		var mouseY = window.innerHeight / 2;

		window.addEventListener(
			"mousemove",
			function (event) {
				mouseX = event.clientX;
				mouseY = event.clientY;
			},
			{ passive: true }
		);

		function loop() {
			orbs.forEach(function (orb) {
				var rect = orb.el.getBoundingClientRect();
				var cx = rect.left + rect.width / 2;
				var cy = rect.top + rect.height / 2;
				var dx = (mouseX - cx) * 0.04 * orb.depth;
				var dy = (mouseY - cy) * 0.04 * orb.depth;

				orb.currentX = (orb.currentX || 0) + (dx - (orb.currentX || 0)) * 0.06;
				orb.currentY = (orb.currentY || 0) + (dy - (orb.currentY || 0)) * 0.06;

				orb.el.style.transform = "translate3d(" + orb.currentX + "px, " + orb.currentY + "px, 0)";
			});

			requestAnimationFrame(loop);
		}

		requestAnimationFrame(loop);
	}

	/* ------------------------------ Custom cursor ------------------------------ */
	function initCustomCursor() {
		if (isMobile || prefersReducedMotion || matchMedia("(hover: none)").matches) return;

		var cursor = document.createElement("div");
		cursor.className = "phx-cursor";
		document.body.appendChild(cursor);

		var targetX = window.innerWidth / 2;
		var targetY = window.innerHeight / 2;
		var currentX = targetX;
		var currentY = targetY;

		window.addEventListener(
			"mousemove",
			function (event) {
				targetX = event.clientX;
				targetY = event.clientY;
				cursor.classList.add("is-visible");
			},
			{ passive: true }
		);

		document.querySelectorAll("a, button, .product-card, .ritual-spotlight__card, .featured-collection__collection").forEach(function (el) {
			el.addEventListener("mouseenter", function () {
				cursor.classList.add("is-active");
			});
			el.addEventListener("mouseleave", function () {
				cursor.classList.remove("is-active");
			});
		});

		function loop() {
			currentX += (targetX - currentX) * 0.18;
			currentY += (targetY - currentY) * 0.18;
			cursor.style.transform = "translate3d(" + currentX + "px, " + currentY + "px, 0)";
			requestAnimationFrame(loop);
		}

		requestAnimationFrame(loop);
	}

	/* ------------------------------ Global dark-mode scroll threshold ------------------------------
	   Rebuilt again (2026-08-28 round 6). What the user actually wants: ONE
	   single point in the scroll, after which the WHOLE homepage -- every
	   section from the hero down to the last one, plus the gaps between them
	   -- is dark; scrolling back above that point returns the WHOLE page to
	   the normal light theme. Not a banded "zone" that starts/reverts at
	   different points for different sections (that read as blocks stacked on
	   each other).

	   Two things had to change from the previous version to get an actually
	   seamless result:
	   1. Every homepage section is now included in the class swap (not just a
	      hand-picked few), still reusing the theme's own dark color scheme
	      ("color-scheme-2", see config/settings_data.json) so text/buttons/
	      prices everywhere stay correctly paired -- same mechanism as before,
	      just applied page-wide.
	   2. The light "gaps" the user was seeing between sections are each
	      section's own external padding/offset settings, rendered on the
	      *`.shopify-section` wrapper* div -- which has no background color of
	      its own, so the page's (light) body color was always showing through
	      there regardless of any per-section scheme swap. Fixed by also
	      toggling background-color on <body> and on every
	      `#MainContent > .shopify-section` wrapper (see the
	      html.phx-dark-page rules in premium-homepage.css) -- so the gaps
	      turn dark right along with everything else. */
	function initDarkZone() {
		if (!hasGsap || prefersReducedMotion) return;

		var thresholdEl = document.querySelector(".featured-collection");
		if (!thresholdEl) return;

		var allSections = document.querySelectorAll("#MainContent > .shopify-section");
		if (!allSections.length) return;

		var schemeClassPattern = /^color-scheme-\d+$/;
		var targets = [];

		allSections.forEach(function (wrapper) {
			var withNested = [wrapper].concat(
				Array.prototype.slice.call(wrapper.querySelectorAll('[class*="color-scheme-"]'))
			);

			withNested.forEach(function (el) {
				var original = Array.prototype.filter.call(el.classList, function (c) {
					return schemeClassPattern.test(c);
				})[0];

				if (original && original !== "color-scheme-2") {
					targets.push({ el: el, original: original });
				}
			});
		});

		/* fix (2026-08-28 round 10): even with matching transition durations,
		   letting dozens of individual elements each animate their own
		   background-color independently is inherently prone to visible
		   micro-desync (different elements, different compositing/paint
		   timing) -- that's what read as a "block type" flash where the gaps
		   changed color a beat before/after the sections. Guaranteed fix:
		   don't animate the colors themselves at all. Cover the screen with a
		   single veil element, swap every class INSTANTLY while hidden behind
		   it, then fade the veil away -- there is now exactly one thing
		   animating, so it is physically impossible for anything to be out of
		   sync with anything else. */
		var veil = document.createElement("div");
		veil.className = "phx-transition-veil";
		document.body.appendChild(veil);

		function readSchemeBackground(schemeClass) {
			var probe = document.createElement("div");
			probe.className = schemeClass;
			probe.style.cssText = "position:absolute;visibility:hidden;pointer-events:none;top:0;left:0;";
			document.body.appendChild(probe);
			var value = getComputedStyle(probe).getPropertyValue("--color-background").trim();
			document.body.removeChild(probe);
			return value;
		}

		var lightBg = getComputedStyle(document.documentElement).getPropertyValue("--color-background").trim() || "#ffffff";
		var darkBg = readSchemeBackground("color-scheme-2") || "#102a2c";

		function setDark(isDark) {
			veil.style.backgroundColor = isDark ? darkBg : lightBg;
			veil.style.opacity = "1";

			/* fix (2026-08-28 round 11): matches the slower 0.6s ease-in-out
			   veil transition in premium-homepage.css -- was 260ms/0.25s,
			   which made the whole thing feel like a flashbulb going off
			   instead of a calm dissolve. Small buffer past the CSS duration
			   so the veil is fully opaque before the instant class swap. */
			window.setTimeout(function () {
				targets.forEach(function (t) {
					if (isDark) {
						t.el.classList.remove(t.original);
						t.el.classList.add("color-scheme-2");
					} else {
						t.el.classList.remove("color-scheme-2");
						t.el.classList.add(t.original);
					}
				});

				document.body.classList.toggle("color-scheme-2", isDark);
				document.documentElement.classList.toggle("phx-dark-page", isDark);

				requestAnimationFrame(function () {
					veil.style.opacity = "0";
				});
			}, 280);
		}

		ScrollTrigger.create({
			trigger: thresholdEl,
			start: "top 50%",
			end: "max",
			onEnter: function () { setDark(true); },
			onEnterBack: function () { setDark(true); },
			onLeaveBack: function () { setDark(false); }
		});
	}

	/* ------------------------------ Init ------------------------------ */
	function init() {
		initLenis();
		initReveal();
		initHero();
		initBanners();
		initFeaturedProducts();
		initProductSettle();
		initTicker();
		initTilt();
		initImageWithText();
		initSocialGallery();
		initRitualSpotlight();
		initClosingBanner();
		initDarkZone();
		initFloatingOrbs();
		initCustomCursor();

		if (hasGsap) {
			ScrollTrigger.refresh();
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
